/* Founder Console primitives. Every one of them can say "I do not know" truthfully. */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { TrendingUp, TrendingDown, Minus, HelpCircle } from "lucide-react";

const DASH = "—";

/* The "?" a Founder can press to find out exactly what a number means, six months from now.
   The explanation is portalled to the console root and fixed to the viewport, so no card,
   strip or scroll container can clip or cover it (iPhone included). */
export const InfoDot = ({ metric, catalog, testId }) => {
  const [open, setOpen] = useState(false); // false | "hover" | "pinned"
  const [pos, setPos] = useState(null);
  const btn = useRef(null);
  const pop = useRef(null);
  const entry = catalog?.[metric];

  useLayoutEffect(() => {
    if (!open || !btn.current) return;
    const r = btn.current.getBoundingClientRect();
    const width = Math.min(290, window.innerWidth - 24);
    const left = Math.max(12, Math.min(r.left + r.width / 2 - width / 2, window.innerWidth - width - 12));
    const below = window.innerHeight - r.bottom > 280 || r.top < 280;
    setPos(below ? { top: r.bottom + 8, left, width } : { bottom: window.innerHeight - r.top + 8, left, width });
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    const outside = (e) => {
      if (!btn.current?.contains(e.target) && !pop.current?.contains(e.target)) close();
    };
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    document.addEventListener("pointerdown", outside);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
      document.removeEventListener("pointerdown", outside);
    };
  }, [open]);

  if (!entry) return null;
  const root = btn.current?.closest(".fc") || document.body;
  return (
    <>
      <button
        ref={btn}
        type="button"
        className="fc-info"
        data-testid={testId || `fc-info-${metric}`}
        aria-label={`What does ${metric} mean?`}
        aria-expanded={!!open}
        onMouseEnter={() => setOpen((o) => o || "hover")}
        onMouseLeave={() => setOpen((o) => (o === "hover" ? false : o))}
        onClick={(e) => { e.stopPropagation(); setOpen((o) => (o === "pinned" ? false : "pinned")); }}
      >
        ?
      </button>
      {open && pos && createPortal(
        <span ref={pop} className="fc-info__pop fc-info__pop--floating" style={pos} role="tooltip"
              data-testid={`fc-info-pop-${metric}`} onClick={(e) => e.stopPropagation()}>
          <b>{metric}</b>
          {entry.definition}
          <dl>
            <dt>source</dt><dd>{(entry.sources || []).join(", ") || DASH}</dd>
            <dt>basis</dt><dd>{entry.basis}</dd>
            <dt>included</dt><dd>{entry.population_included}</dd>
            <dt>excluded</dt><dd>{entry.population_excluded}</dd>
            <dt>period</dt><dd>{entry.period}</dd>
          </dl>
        </span>,
        root,
      )}
    </>
  );
};

export const fmt = (n) => {
  if (n === null || n === undefined) return DASH;
  if (typeof n !== "number") return String(n);
  return Number.isInteger(n) ? n.toLocaleString("en-US") : n.toLocaleString("en-US", { maximumFractionDigits: 1 });
};

/* A metric value that refuses to pretend. Unavailable, suppressed and insufficient each say so. */
export const Figure = ({ env, suffix = "", className = "fc-metric__value", testId }) => {
  if (!env) return <div className={className} data-testid={testId}>{DASH}</div>;
  const missing = !env.available || env.value === null || env.value === undefined;
  return (
    <div className={className} data-testid={testId}>
      {env.current_only
        ? <span className="fc-current-only">Current only</span>
        : missing ? <span className="fc-unavail">{DASH}</span> : `${fmt(env.value)}${suffix}`}
    </div>
  );
};

/* Why a value is missing, or why a movement is not yet a trend. Always the reason, never silence. */
export const Reason = ({ env, testId }) => {
  if (!env) return null;
  const missing = !env.available || env.value === null || env.value === undefined;
  const text = env.suppressed
    ? "Suppressed: too few members to show safely"
    : missing
      ? (env.note || "Not measurable from authoritative data")
      : (!env.sufficient ? env.note : (env.change && !env.change.sufficient_for_trend ? env.change.note : null));
  if (!text) return null;
  return <div className="fc-metric__foot" data-testid={testId}>{text}</div>;
};

export const Delta = ({ env, comparison, testId }) => {
  const change = env?.change;
  if (!change || env.value === null || env.value === undefined) return null;
  const Icon = change.direction === "up" ? TrendingUp : change.direction === "down" ? TrendingDown : Minus;
  const pct = change.delta_pct === null || change.delta_pct === undefined
    ? `${change.delta > 0 ? "+" : ""}${fmt(change.delta)}`
    : `${change.delta_pct > 0 ? "+" : ""}${change.delta_pct}%`;
  return (
    <div className="fc-metric__foot" data-testid={testId}>
      <span className="fc-delta" data-dir={change.direction} data-soft={!change.sufficient_for_trend}>
        <Icon /> {pct}
      </span>
      <span style={{ marginLeft: 7 }}>
        vs. {comparison ? `prior ${comparison}` : "prior period"}
        {!change.sufficient_for_trend ? " (below trend threshold)" : ""}
      </span>
    </div>
  );
};

export const MetricCard = ({ icon: Icon, label, env, metric, catalog, comparison, gold, suffix, testId }) => (
  <div className={`fc-panel fc-metric${gold ? " fc-metric--gold" : ""}`} data-testid={testId}>
    <div className="fc-metric">
      <div className="fc-metric__icon"><Icon /></div>
      <div style={{ minWidth: 0 }}>
        <div className="fc-metric__label">
          {label}
          <InfoDot metric={metric} catalog={catalog} />
        </div>
        <Figure env={env} suffix={suffix} testId={`${testId}-value`} />
        <Delta env={env} comparison={comparison} testId={`${testId}-delta`} />
        <Reason env={env} testId={`${testId}-reason`} />
      </div>
    </div>
  </div>
);

export const Panel = ({ title, icon: Icon, sub, control, children, testId, className = "" }) => (
  <section className={`fc-panel ${className}`} data-testid={testId}>
    <div className="fc-panel__head">
      <div style={{ minWidth: 0 }}>
        <h2 className="fc-panel__title">
          {Icon ? <Icon style={{ verticalAlign: "-3px", marginRight: 8 }} /> : null}
          {title}
        </h2>
        {sub ? <p className="fc-panel__sub">{sub}</p> : null}
      </div>
      {control ? <div className="fc-panel__control">{control}</div> : null}
    </div>
    {children}
  </section>
);

export const Spark = ({ series, gold, testId }) => {
  const values = (series || []).map((v) => (typeof v === "number" ? v : 0));
  const max = Math.max(1, ...values);
  return (
    <div className={`fc-spark${gold ? " fc-spark--gold" : ""}`} data-testid={testId}>
      {values.map((v, i) => <i key={i} style={{ height: `${Math.max(6, (v / max) * 100)}%` }} />)}
    </div>
  );
};

/* A ring that draws nothing rather than a comforting arc when there is no number.
   The arc is SVG with dynamic attributes; the number is HTML, so it is never at the mercy of
   how dynamic children are wrapped inside an <svg>. */
export const Ring = ({ env, label, suffix = "", tone = "var(--fc-teal)", arc = true, testId }) => {
  const missing = !env?.available || env?.value === null || env?.value === undefined;
  const pct = missing ? 0 : Math.max(0, Math.min(100, typeof env.value === "number" ? env.value : 0));
  const r = 32;
  const c = 2 * Math.PI * r;
  const drawArc = arc && !missing;
  return (
    <div className="fc-ring" data-testid={testId}>
      <div style={{ position: "relative", width: 78, height: 78, margin: "0 auto" }}>
        <svg viewBox="0 0 78 78" style={{ width: 78, height: 78 }}>
          <circle cx="39" cy="39" r={r} fill="none" stroke="rgba(168,186,220,0.12)" strokeWidth="4" />
          <circle
            cx="39" cy="39" r={r} fill="none" stroke={drawArc ? tone : "transparent"}
            strokeWidth="4" strokeLinecap="round"
            strokeDasharray={`${(pct / 100) * c} ${c}`} transform="rotate(-90 39 39)"
          />
        </svg>
        <div
          style={{
            position: "absolute", inset: 0, display: "grid", placeItems: "center",
            fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 15,
            color: "var(--fc-ivory)",
          }}
          data-testid={`${testId}-value`}
        >
          {missing ? DASH : `${fmt(env.value)}${suffix}`}
        </div>
      </div>
      <div className="fc-ring__label">{label}</div>
      <div className="fc-ring__state" data-testid={`${testId}-state`}>
        {env?.current_only ? "Current only"
          : missing ? (env?.note ? "Not measurable" : "No data") : "Measured"}
      </div>
    </div>
  );
};

export const Chip = ({ children, tone, testId }) => (
  <span className="fc-chip" data-tone={tone} data-testid={testId}>{children}</span>
);

export const Note = ({ children, testId }) => (
  <p className="fc-note" data-testid={testId}>{children}</p>
);

// Computation time, not data-update time; sources come from the metric catalogue.
export const FreshnessNote = ({ freshness }) => {
  if (!freshness?.generated_at) return null;
  const at = new Date(freshness.generated_at);
  const time = Number.isNaN(at.getTime()) ? freshness.generated_at
    : `${at.toISOString().slice(0, 16).replace("T", " ")} UTC`;
  return (
    <Note testId="fc-freshness">
      Computed {time} · {freshness.mode} · sources: {(freshness.sources || []).join(", ") || DASH}
      {" "}<span style={{ color: "var(--fc-mute)" }}>(computation time — not when the underlying data was last updated)</span>
    </Note>
  );
};

export { DASH, HelpCircle };
